import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Clock, Loader2, XCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const { t } = useLanguage();
  const p = t.payment;
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("processing");
  const booking = (() => {
    try { return JSON.parse(sessionStorage.getItem("oin-last-booking") || "null"); } catch { return null; }
  })();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
      } catch { /* keep polling */ }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const content = {
    processing: { icon: <Loader2 size={26} className="animate-spin" />, title: p.processingTitle, text: p.processingText, tone: "bg-ink" },
    paid: { icon: <Check size={26} />, title: p.successTitle, text: p.successText, tone: "bg-rouge" },
    pending: { icon: <Clock size={26} />, title: p.pendingTitle, text: p.pendingText, tone: "bg-ink" },
    failed: { icon: <XCircle size={26} />, title: p.failTitle, text: p.failText, tone: "bg-ink" },
  }[status];

  return (
    <section className="mx-auto flex min-h-screen max-w-[1400px] items-center px-6 lg:px-12" data-testid="payment-result">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-xl border border-ink/15 bg-sand p-10 lg:p-14"
      >
        <span className={`inline-flex h-14 w-14 items-center justify-center rounded-full text-cream ${content.tone}`} data-testid="payment-status-icon">
          {content.icon}
        </span>
        <h1 className="mt-8 font-display text-3xl font-bold lg:text-4xl" data-testid="payment-status-title">{content.title}</h1>
        <p className="mt-4 text-sm leading-relaxed text-stone2">{content.text}</p>
        {booking && (
          <dl className="mt-8 space-y-2 border-t border-ink/10 pt-6 text-sm" data-testid="payment-booking-summary">
            <div className="flex justify-between gap-6"><dt className="text-stone2">{t.services.form.service}</dt><dd className="font-semibold">{t.services.list.find((s) => s.id === booking.service)?.name || booking.service}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-stone2">{t.services.form.date}</dt><dd className="font-semibold">{booking.date}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-stone2">{t.services.form.time}</dt><dd className="font-semibold">{booking.time}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-stone2">{p.refLabel}</dt><dd className="font-semibold">{booking.id.slice(0, 8).toUpperCase()}</dd></div>
          </dl>
        )}
        <Link to="/" data-testid="payment-home-button" className="mt-10 inline-block border border-ink px-7 py-3 text-xs font-bold uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-ink hover:text-cream">
          {p.backHome}
        </Link>
      </motion.div>
    </section>
  );
};

export default PaymentSuccess;
